import type { Theme, Language, Department, QuizProgress } from './types';

const THEME_KEY = 'med_mcq_theme';
const LANG_KEY = 'med_mcq_lang';
const WIZARD_KEY = 'med_mcq_wizard_selection';
const THEORY_DESKTOP_OPEN_KEY = 'med_mcq_theory_open';
const THEORY_FONT_SIZE_KEY = 'med_mcq_theory_font_size';
const PROGRESS_KEY_PREFIX = 'med_mcq_quiz_progress_';

export interface SavedWizardSelection {
  wilaya?: string;
  facultySlug?: string;
  year?: Department;
  subjectId?: string;
}

export function getSavedTheme(): Theme {
  try {
    const val = localStorage.getItem(THEME_KEY) as Theme;
    if (val === 'light' || val === 'cream' || val === 'dark') {
      return val;
    }
  } catch (e) {}

  if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

export function saveTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
    document.documentElement.setAttribute('data-theme', theme);
  } catch (e) {}
}

export function saveLanguage(lang: Language): void {
  try {
    localStorage.setItem(LANG_KEY, lang);
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
  } catch (e) {}
}

export function getSavedWizard(): SavedWizardSelection {
  try {
    const raw = localStorage.getItem(WIZARD_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {}
  return {};
}

export function saveWizardSelection(selection: SavedWizardSelection): void {
  try {
    localStorage.setItem(WIZARD_KEY, JSON.stringify(selection));
  } catch (e) {}
}

export function getSavedQuizProgress(lessonKey: string, wilaya: string): QuizProgress | null {
  try {
    const raw = localStorage.getItem(`${PROGRESS_KEY_PREFIX}${lessonKey}_${wilaya}`);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {}
  return null;
}

export function saveQuizProgress(lessonKey: string, wilaya: string, progress: QuizProgress): void {
  try {
    localStorage.setItem(`${PROGRESS_KEY_PREFIX}${lessonKey}_${wilaya}`, JSON.stringify(progress));
  } catch (e) {}
}

export function clearQuizProgress(lessonKey: string, wilaya: string): void {
  try {
    localStorage.removeItem(`${PROGRESS_KEY_PREFIX}${lessonKey}_${wilaya}`);
  } catch (e) {}
}

export function getSavedTheoryOpenDesktop(): boolean {
  try {
    const val = localStorage.getItem(THEORY_DESKTOP_OPEN_KEY);
    if (val !== null) {
      return val === 'true';
    }
  } catch (e) {}
  return true; // Default open on desktop if lesson has theory
}

export function saveTheoryOpenDesktop(isOpen: boolean): void {
  try {
    localStorage.setItem(THEORY_DESKTOP_OPEN_KEY, String(isOpen));
  } catch (e) {}
}

export function getSavedTheoryFontSize(): number {
  try {
    const val = localStorage.getItem(THEORY_FONT_SIZE_KEY);
    if (val) {
      const parsed = parseInt(val, 10);
      if (parsed >= 13 && parsed <= 24) {
        return parsed;
      }
    }
  } catch (e) {}
  return 16;
}

export function saveTheoryFontSize(size: number): void {
  try {
    localStorage.setItem(THEORY_FONT_SIZE_KEY, String(size));
  } catch (e) {}
}

const RECENT_SESSIONS_KEY = 'med_mcq_recent_sessions';
const SIDEBAR_OPEN_KEY = 'med_mcq_sidebar_open';

export function getSavedSidebarOpen(): boolean {
  try {
    const val = localStorage.getItem(SIDEBAR_OPEN_KEY);
    if (val !== null) {
      return val === 'true';
    }
  } catch (e) {}
  return true; // Default open on desktop
}

export function saveSidebarOpen(open: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_OPEN_KEY, String(open));
  } catch (e) {}
}

export function getRecentSessions(): import('./types').RecentSession[] {
  try {
    const raw = localStorage.getItem(RECENT_SESSIONS_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {}
  return [];
}

export function saveRecentSession(session: import('./types').RecentSession): void {
  try {
    const existing = getRecentSessions().filter(
      (s) => !(s.lessonId === session.lessonId && s.wilaya === session.wilaya)
    );
    const updated = [session, ...existing].slice(0, 15);
    localStorage.setItem(RECENT_SESSIONS_KEY, JSON.stringify(updated));
  } catch (e) {}
}

export function deleteRecentSession(lessonId: string, wilaya: string): import('./types').RecentSession[] {
  try {
    const existing = getRecentSessions();
    const updated = existing.filter(
      (s) => !(s.lessonId === lessonId && s.wilaya === wilaya)
    );
    localStorage.setItem(RECENT_SESSIONS_KEY, JSON.stringify(updated));
    clearQuizProgress(lessonId, wilaya);
    return updated;
  } catch (e) {
    return [];
  }
}

export function clearAllRecentSessions(): void {
  try {
    localStorage.removeItem(RECENT_SESSIONS_KEY);
  } catch (e) {}
}

export function getSavedYear(): Department | null {
  const wizard = getSavedWizard();
  return wizard.year || null;
}

export function saveSelectedYear(year: Department): void {
  const wizard = getSavedWizard();
  saveWizardSelection({ ...wizard, year });
}

export function getSavedWilaya(): string {
  const wizard = getSavedWizard();
  return wizard.wilaya || 'all';
}

export function saveSelectedWilaya(wilaya: string): void {
  const wizard = getSavedWizard();
  saveWizardSelection({ ...wizard, wilaya });
}

export function getSavedLessonAnnotations(lessonId: string): import('./types').LessonAnnotations {
  try {
    const raw = localStorage.getItem(`med_mcq_annotations_${lessonId}`);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return { strokes: [], notes: [] };
}

export function saveLessonAnnotations(lessonId: string, data: import('./types').LessonAnnotations): void {
  try {
    localStorage.setItem(`med_mcq_annotations_${lessonId}`, JSON.stringify(data));
  } catch (e) {}
}

export function clearLessonAnnotations(lessonId: string): void {
  try {
    localStorage.removeItem(`med_mcq_annotations_${lessonId}`);
  } catch (e) {}
}

const MINDMAPS_STORAGE_KEY = 'med_mcq_saved_mindmaps';
const ACTIVE_MINDMAP_KEY = 'med_mcq_active_mindmap_id';

export function getDefaultMindmapTemplates(): import('./types').MindmapDocument[] {
  return [
    {
      id: 'template_chest_pain',
      title: 'تشخيص ألم الصدر الحاد (Douleur Thoracique)',
      updatedAt: Date.now(),
      nodes: [
        {
          id: 'node_root',
          x: 440,
          y: 280,
          title: 'Douleur Thoracique Aiguë',
          subtitle: 'ألم الصدر الحاد (الفرز والتشخيص)',
          notes: 'حالة إسعافية: البحث الفوري عن الحالات الـ 5 المهددة للحياة (PIED).',
          color: '#0284c7',
          textColor: '#ffffff',
          shape: 'card',
          width: 250,
        },
        {
          id: 'node_sca',
          x: 80,
          y: 80,
          title: 'Syndrome Coronarien Aigu (SCA)',
          subtitle: 'متلازمة الشريان التاجي الحادة',
          notes: '• ألم خلف القص عاصر يمتد للذراع والفك\n• تخطيط قلب فوري (ECG < 10 min)\n• التروبونين فائق الحساسية',
          color: '#dc2626',
          textColor: '#ffffff',
          shape: 'card',
          width: 240,
        },
        {
          id: 'node_dissection',
          x: 800,
          y: 80,
          title: 'Dissection Aortique',
          subtitle: 'تسلخ الشريان الأبهر',
          notes: '• ألم مباغت تمزقي ينتشر للظهر بين اللوحين\n• اختلاف النبض والضغط بين الذراعين\n• أشعة مقطعية عاجلة (Angio-TDM)',
          color: '#ea580c',
          textColor: '#ffffff',
          shape: 'card',
          width: 240,
        },
        {
          id: 'node_ep',
          x: 80,
          y: 480,
          title: 'Embolie Pulmonaire (EP)',
          subtitle: 'الانصمام الرئوي',
          notes: '• ألم جنبي حاد + ضيق تنفس مباغت\n• تسرع ضربات القلب ونقص الأكسجة\n• تقييم سكور ويلز (Score de Wells)',
          color: '#d97706',
          textColor: '#ffffff',
          shape: 'card',
          width: 240,
        },
        {
          id: 'node_pericardite',
          x: 800,
          y: 480,
          title: 'Péricardite Aiguë',
          subtitle: 'التهاب التامور الحاد',
          notes: '• ألم يزداد بالشهيق ويخف بالانحناء للأمام\n• احتكاك تاموري مميز\n• ارتفاع مقعر منتشر لقطعة ST بالـ ECG',
          color: '#7c3aed',
          textColor: '#ffffff',
          shape: 'card',
          width: 240,
        },
        {
          id: 'node_pneumothorax',
          x: 440,
          y: 560,
          title: 'Pneumothorax Spontané',
          subtitle: 'استرواح الصدر التلقائي',
          notes: '• طبلية قرعية وغياب أصوات التنفس\n• صورة الصدر الشعاعية (Radio Thorax)',
          color: '#059669',
          textColor: '#ffffff',
          shape: 'pill',
          width: 250,
        },
      ],
      edges: [
        {
          id: 'edge_1',
          from: 'node_root',
          to: 'node_sca',
          label: 'ECG + Troponine',
          style: 'bezier',
          stroke: 'solid',
          arrowHead: 'arrow',
          color: '#dc2626',
          width: 2.5,
        },
        {
          id: 'edge_2',
          from: 'node_root',
          to: 'node_dissection',
          label: 'Angio-TDM urgent',
          style: 'bezier',
          stroke: 'pulse',
          arrowHead: 'arrow',
          color: '#ea580c',
          width: 2.5,
        },
        {
          id: 'edge_3',
          from: 'node_root',
          to: 'node_ep',
          label: 'Wells / D-Dimères',
          style: 'bezier',
          stroke: 'solid',
          arrowHead: 'arrow',
          color: '#d97706',
          width: 2.5,
        },
        {
          id: 'edge_4',
          from: 'node_root',
          to: 'node_pericardite',
          label: 'Auscultation / ST',
          style: 'bezier',
          stroke: 'dashed',
          arrowHead: 'arrow',
          color: '#7c3aed',
          width: 2.5,
        },
        {
          id: 'edge_5',
          from: 'node_root',
          to: 'node_pneumothorax',
          label: 'Auscultation unilatérale',
          style: 'step',
          stroke: 'dotted',
          arrowHead: 'circle',
          color: '#059669',
          width: 2,
        },
      ],
    },
    {
      id: 'template_pharmaco',
      title: 'خريطة أدوية ارتفاع ضغط الدم (Antihypertenseurs)',
      updatedAt: Date.now() - 3600000,
      nodes: [
        {
          id: 'p_root',
          x: 440,
          y: 260,
          title: 'Antihypertenseurs Majeurs',
          subtitle: 'الأصناف العلاجية الرئيسية لارتفاع ضغط الدم',
          notes: 'قواعد المعالجة الأولية: أحادية ثم ثنائية التوليف.',
          color: '#0284c7',
          textColor: '#ffffff',
          shape: 'card',
          width: 260,
        },
        {
          id: 'p_iec',
          x: 100,
          y: 100,
          title: 'IEC / ARA II',
          subtitle: 'مضادات نظام الرينين-أنجيوتنسين',
          notes: '• كابتوبريل، راميبريل، فالسارتان\n• حماية كلوية وقلبية ممتازة\n• محظورة مطلقاً أثناء الحمل!',
          color: '#059669',
          textColor: '#ffffff',
          shape: 'card',
          width: 230,
        },
        {
          id: 'p_calcium',
          x: 780,
          y: 100,
          title: 'Inhibiteurs Calciques (IC)',
          subtitle: 'حاصرات قنوات الكالسيوم',
          notes: '• أملوديبين، نيكارديبين\n• توسيع الشرايين المحيطية\n• أثر جانبي: وذمة الأطراف السفلية',
          color: '#d97706',
          textColor: '#ffffff',
          shape: 'card',
          width: 230,
        },
        {
          id: 'p_diuretic',
          x: 100,
          y: 440,
          title: 'Diurétiques Thiazidiques',
          subtitle: 'المدرات الثيازيدية',
          notes: '• هيدروكلوروثيازيد، إنداباميد\n• طرد الصوديوم والماء وخفض المقاومة\n• مراقبة البوتاسيوم وحمض اليوريك',
          color: '#7c3aed',
          textColor: '#ffffff',
          shape: 'card',
          width: 230,
        },
        {
          id: 'p_beta',
          x: 780,
          y: 440,
          title: 'Bêta-Bloquants (BB)',
          subtitle: 'حاصرات مستقبلات بيتا',
          notes: '• بيسوبرولول، ميتوبرولول\n• استطباب نوعي: قصور القلب، ما بعد الجلطة\n• تخفيض سرعة النبض واستهلاك الأكسجين',
          color: '#dc2626',
          textColor: '#ffffff',
          shape: 'card',
          width: 230,
        },
      ],
      edges: [
        {
          id: 'pe_1',
          from: 'p_root',
          to: 'p_iec',
          label: 'Inhibition du SRAA',
          style: 'bezier',
          stroke: 'solid',
          arrowHead: 'arrow',
          color: '#059669',
          width: 2.5,
        },
        {
          id: 'pe_2',
          from: 'p_root',
          to: 'p_calcium',
          label: 'Vasodilatation artérielle',
          style: 'bezier',
          stroke: 'solid',
          arrowHead: 'arrow',
          color: '#d97706',
          width: 2.5,
        },
        {
          id: 'pe_3',
          from: 'p_root',
          to: 'p_diuretic',
          label: 'Natriurèse',
          style: 'bezier',
          stroke: 'solid',
          arrowHead: 'arrow',
          color: '#7c3aed',
          width: 2.5,
        },
        {
          id: 'pe_4',
          from: 'p_root',
          to: 'p_beta',
          label: 'Chronotrope (-)',
          style: 'bezier',
          stroke: 'solid',
          arrowHead: 'arrow',
          color: '#dc2626',
          width: 2.5,
        },
      ],
    },
  ];
}

export function createBlankMindmap(): import('./types').MindmapDocument {
  return {
    id: `mindmap_${Date.now()}`,
    title: 'خريطة ذهنية جديدة',
    updatedAt: Date.now(),
    nodes: [],
    edges: [],
  };
}

export function getSavedMindmaps(): import('./types').MindmapDocument[] {
  try {
    const raw = localStorage.getItem(MINDMAPS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {}

  const initial = [createBlankMindmap()];
  saveMindmaps(initial);
  return initial;
}

export function saveMindmaps(maps: import('./types').MindmapDocument[]): void {
  try {
    localStorage.setItem(MINDMAPS_STORAGE_KEY, JSON.stringify(maps));
  } catch (e) {}
}

export function getActiveMindmapId(): string {
  try {
    const id = localStorage.getItem(ACTIVE_MINDMAP_KEY);
    if (id) return id;
  } catch (e) {}
  return 'mindmap_blank_1';
}

export function saveActiveMindmapId(id: string): void {
  try {
    localStorage.setItem(ACTIVE_MINDMAP_KEY, id);
  } catch (e) {}
}



